import CustomerAvatar from '../components/CustomerAvatar';
import type { CustomerDetail } from '../hooks/useCustomerDetail';
import { getTypeColor } from '../utils/typeColors';
import CustomerOrders from './components/CustomerOrders';

interface ViewPanelProps {
  detail: CustomerDetail;
  customerTypeName: string | null;
}

interface FieldSpec {
  icon: string;
  label: string;
  value?: string | null;
  href?: string;
}

/** 一行资料。没填就整行不出——补一个 '—' 是「占住位置」，不是「告诉你没填」 */
const Field = ({ icon, label, value, href }: FieldSpec) => {
  if (!value) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={`${icon} text-gray-400 text-xs`} />
        </div>
        <span className="text-xs text-gray-400">{label}</span>
      </div>
      {href ? (
        <a href={href} className="text-sm font-medium text-teal-600 hover:underline break-all block pl-5">
          {value}
        </a>
      ) : (
        <p className="text-sm font-medium text-gray-800 break-all pl-5">{value}</p>
      )}
    </div>
  );
};

const SectionHead = ({ icon, title }: { icon: string; title: string }) => (
  <div className="flex items-center gap-2 pt-4 pb-3 border-t border-gray-50 first:pt-0 first:border-t-0">
    <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded shrink-0">
      <i className={`${icon} text-teal-500 text-xs`} />
    </div>
    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
  </div>
);

/** 一组资料：整组都没填时连标题一起撤下，标题下摆一片空白等于把「没资料」伪装成「已填完」 */
const InfoGroup = ({
  icon,
  title,
  fields,
}: {
  icon: string;
  title: string;
  fields: FieldSpec[];
}) => {
  const filled = fields.filter((f) => !!f.value);
  if (filled.length === 0) return null;
  return (
    <>
      <SectionHead icon={icon} title={title} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 md:gap-y-4">
        {filled.map((f) => (
          <Field key={f.label} {...f} />
        ))}
      </div>
    </>
  );
};

export default function CustomerViewPanel({ detail, customerTypeName }: ViewPanelProps) {
  const hasContact = !!(detail.phone || detail.email);
  const typeColor = customerTypeName ? getTypeColor(customerTypeName) : null;
  const tags = detail.tags ?? [];
  const hasTypeOrTags = !!customerTypeName || tags.length > 0;
  // 五组资料一行都没有：卡片不留空壳，摆一句去编辑的引导
  const hasAnyInfo = !!(
    detail.phone ||
    detail.backupPhone ||
    detail.email ||
    detail.companyName ||
    detail.idCard ||
    detail.location ||
    detail.address ||
    detail.emergencyContact ||
    detail.emergencyPhone ||
    detail.introduction
  );

  return (
    <div className="space-y-4 md:space-y-5">

      {/* ── Profile card ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
        <div className="flex items-center gap-3 md:gap-5">
          {/* Avatar */}
          <div className="shrink-0">
            <CustomerAvatar name={detail.fullName} avatarUrl={detail.avatar} size="xl" className="ring-2 ring-amber-400" />
          </div>

          {/* Name / company / badges */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg md:text-2xl font-bold text-gray-900 truncate">
                {detail.fullName ?? '未命名客户'}
              </h2>
              {/* 客户是否已在本系统注册账号 */}
              {detail.isUser ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-emerald-50 text-emerald-600 border border-emerald-200 shrink-0">
                  <div className="w-3 h-3 flex items-center justify-center shrink-0">
                    <i className="ri-shield-check-line" />
                  </div>
                  已注册用户
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium bg-rose-50 text-rose-600 border border-rose-200 shrink-0">
                  <div className="w-3 h-3 flex items-center justify-center shrink-0">
                    <i className="ri-user-line" />
                  </div>
                  非注册用户
                </span>
              )}
              {/* 档案状态：激活（功能正常）/ 停用（可修改原因后恢复）/ 禁用（需管理员解除，可申诉） */}
              {detail.status && (
                <span
                  title={
                    detail.status === '停用'
                      ? `已停用：${detail.stopReason || '未填写原因'}（修正原因后自动激活）`
                      : detail.status === '禁用'
                        ? `已被管理员禁用：${detail.stopReason || '未填写原因'}（如需恢复请提交申诉）`
                        : undefined
                  }
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium border shrink-0 ${
                    detail.status === '激活'
                      ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                      : detail.status === '停用'
                        ? 'bg-amber-50 text-amber-600 border-amber-200'
                        : detail.status === '禁用'
                          ? 'bg-rose-50 text-rose-600 border-rose-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                  }`}
                >
                  <div className="w-3 h-3 flex items-center justify-center shrink-0">
                    <i
                      className={
                        detail.status === '激活'
                          ? 'ri-checkbox-circle-line'
                          : detail.status === '停用'
                            ? 'ri-pause-circle-line'
                            : detail.status === '禁用'
                              ? 'ri-forbid-line'
                              : 'ri-question-line'
                      }
                    />
                  </div>
                  {detail.status}
                </span>
              )}
            </div>
              {detail.companyName && (
                <p className="text-xs md:text-sm text-gray-400 mt-0.5 truncate">{detail.companyName}</p>
              )}
              {/* 辅助称呼：该客户注册升格时姓名与原档案有出入，系统自动生成（仅客户页显示） */}
              {detail.displayName && detail.displayName !== detail.fullName && (
                <p className="text-xs md:text-sm text-slate-400 mt-0.5">称呼：{detail.displayName}</p>
              )}
            {/* 类型与标签一个都没有时整行不出：摆一句「暂无」只是把空位再占一次 */}
            {hasTypeOrTags && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {customerTypeName && typeColor && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium"
                    style={{
                      backgroundColor: typeColor.bg,
                      color: typeColor.text,
                      border: `1px solid ${typeColor.border}`,
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: typeColor.dot }}
                    />
                    {customerTypeName}
                  </span>
                )}
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: `${tag.color ?? '#14b8a6'}18`,
                      color: tag.color ?? '#14b8a6',
                      border: `1px solid ${tag.color ?? '#14b8a6'}40`,
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tag.color ?? '#14b8a6' }} />
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Meta - desktop only */}
          <div className="hidden md:block shrink-0 text-right">
            <p className="text-xs text-gray-400">客户编号</p>
            <p className="text-xs text-gray-500 font-mono mt-0.5" title={detail.id}>
              {detail.id.slice(0, 8).toUpperCase()}
            </p>
            {detail.createdAt && (
              <>
                <p className="text-xs text-gray-400 mt-2">创建于</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(detail.createdAt).toLocaleDateString('zh-CN')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Meta strip - mobile only */}
        <div className="md:hidden flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-hashtag text-gray-300 text-xs" />
            </div>
            <span className="text-xs text-gray-400 font-mono">{detail.id.slice(0, 8).toUpperCase()}</span>
          </div>
          {detail.createdAt && (
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-line text-gray-300 text-xs" />
              </div>
              <span className="text-xs text-gray-400">
                {new Date(detail.createdAt).toLocaleDateString('zh-CN')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile quick actions ────────────────────────────────── */}
      {hasContact && (
        <div className="md:hidden flex gap-3">
          {detail.phone && (
            <a
              href={`tel:${detail.phone}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line" />
              </div>
              拨打电话
            </a>
          )}
          {detail.email && (
            <a
              href={`mailto:${detail.email}`}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 hover:border-teal-300 text-gray-600 hover:text-teal-600 text-sm font-medium rounded-xl transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line" />
              </div>
              发送邮件
            </a>
          )}
        </div>
      )}

      {/* ── Detail area ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-start">

        {/* Main info card */}
        <div className="md:col-span-2 bg-white rounded-xl border border-gray-100 p-4 md:p-6 space-y-0">

          <InfoGroup
            icon="ri-contacts-line"
            title="联系方式"
            fields={[
              { icon: 'ri-phone-line', label: '手机号码', value: detail.phone, href: detail.phone ? `tel:${detail.phone}` : undefined },
              { icon: 'ri-smartphone-line', label: '备用号码', value: detail.backupPhone, href: detail.backupPhone ? `tel:${detail.backupPhone}` : undefined },
              { icon: 'ri-mail-line', label: '电子邮箱', value: detail.email, href: detail.email ? `mailto:${detail.email}` : undefined },
            ]}
          />

          <InfoGroup
            icon="ri-user-line"
            title="个人信息"
            fields={[
              { icon: 'ri-building-line', label: '公司/单位', value: detail.companyName },
              // 身份证号由本人注册时产生，下发恒为掩码值，只在详情页只读展示
              { icon: 'ri-id-card-line', label: '身份证号', value: detail.idCard },
            ]}
          />

          <InfoGroup
            icon="ri-map-pin-line"
            title="地址信息"
            fields={[
              { icon: 'ri-map-pin-2-line', label: '所在城市', value: detail.location },
              { icon: 'ri-road-map-line', label: '详细地址', value: detail.address },
            ]}
          />

          <InfoGroup
            icon="ri-shield-user-line"
            title="紧急联系人"
            fields={[
              { icon: 'ri-user-heart-line', label: '联系人姓名', value: detail.emergencyContact },
              { icon: 'ri-phone-line', label: '联系人电话', value: detail.emergencyPhone, href: detail.emergencyPhone ? `tel:${detail.emergencyPhone}` : undefined },
            ]}
          />

          {detail.introduction && (
            <>
              <SectionHead icon="ri-file-text-line" title="备注信息" />
              <div className="pl-5">
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{detail.introduction}</p>
              </div>
            </>
          )}

          {/* 五组全空：整张卡就是一句引导，不铺一排破折号 */}
          {!hasAnyInfo && (
            <p className="text-sm text-gray-400">还没有填写任何资料，点右上方「编辑」补充</p>
          )}
        </div>

        {/* Sidebar：三张卡都是「有一项才出一张」，没内容就不占栏位（对齐 hmos 与 Flutter 详情） */}
        <div className="md:col-span-1 grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-4">

          {/* Customer Type */}
          {customerTypeName && typeColor && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">客户类型</h3>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg font-medium"
                style={{
                  backgroundColor: typeColor.bg,
                  color: typeColor.text,
                  border: `1px solid ${typeColor.border}`,
                }}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: typeColor.dot }}
                />
                {customerTypeName}
              </span>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">客户标签</h3>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium"
                    style={{
                      backgroundColor: `${tag.color ?? '#14b8a6'}18`,
                      color: tag.color ?? '#14b8a6',
                      border: `1px solid ${tag.color ?? '#14b8a6'}40`,
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tag.color ?? '#14b8a6' }} />
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Quick actions - desktop only */}
          {hasContact && (
            <div className="hidden md:block bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">快速联系</h3>
              <div className="space-y-2">
                {detail.phone && (
                  <a
                    href={`tel:${detail.phone}`}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50 hover:bg-teal-50 hover:text-teal-600 text-sm text-gray-600 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-xs" />
                    </div>
                    <span className="truncate">{detail.phone}</span>
                  </a>
                )}
                {detail.email && (
                  <a
                    href={`mailto:${detail.email}`}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50 hover:bg-teal-50 hover:text-teal-600 text-sm text-gray-600 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-xs" />
                    </div>
                    <span className="truncate">{detail.email}</span>
                  </a>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── 订单记录 ── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 flex items-center justify-center bg-teal-50 rounded shrink-0">
            <i className="ri-file-list-3-line text-teal-500 text-xs" />
          </div>
          <h2 className="text-sm font-semibold text-gray-700">订单记录</h2>
        </div>
        <CustomerOrders customerId={detail.id} customerName={detail.fullName ?? undefined} />
      </div>
    </div>
  );
}
